import { Paper as MuiPaper } from '@material-ui/core';
import { ColorDynamic, useResponsiveValue } from '@superdispatch/ui';
import {
  ReactChild,
  ReactNode,
  RefObject,
  Suspense,
  useLayoutEffect,
  useRef,
} from 'react';
import { ContentProgressIndicator } from 'shared/layout/ContentProgressIndicator';
import {
  NAVIGATION_HEADER_BANNER_HEIGHT_CSS_VAR,
  resetNavigationBottomBarHeight,
  resetNavigationContentBannerHeight,
  setNavigationBottomBarHeight,
  setNavigationContentBannerHeight,
} from 'shared/layout/NavigationHeaderBannerHeight';
import styled, { css } from 'styled-components';

const pageMinHeight = css`
  min-height: calc(100vh - var(--navigation-header-banner-height, 0px));
`;

const NavigationBarContainerRoot = styled.div`
  display: flex;

  width: 100%;
  min-width: 100%;
  max-width: 100%;

  ${pageMinHeight};

  @media (max-width: 960px) {
    padding-bottom: 50px;
  }
`;

const NavigationBarContainerMain = styled.div`
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: contents;
  }
`;

const NavigationBarContainerContent = styled.div`
  position: relative;
  ${pageMinHeight};

  & [data-visibility],
  & [data-visibility] > * {
    ${pageMinHeight};
  }

  width: 100%;
  max-width: 100%;
  min-width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: calc(100% - 88px);
    max-width: calc(100% - 88px);
    min-width: calc(100% - 88px);
  }

  @media print {
    width: 100%;
    max-width: 100%;
    min-width: 100%;
  }
`;

const NavigationBarContainerBottom = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;

  @media print {
    display: none;
  }
`;

const NavigationBarContainerBanner = styled.div`
  position: sticky;
  top: var(${NAVIGATION_HEADER_BANNER_HEIGHT_CSS_VAR}, 0px);
  z-index: 1200;

  @media print {
    display: none;
  }
`;

const Paper = styled(MuiPaper)`
  height: 100%;
  overflow-x: auto;
  border: none;
  background-color: ${ColorDynamic.Silver200};

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: contents;
  }
`;

interface NavigationBarBottomProps {
  children: ReactNode;
}

// The bottom bar is fixed, so pages sized with `viewportHeight()` need its
// height to keep their bottom content from being covered.
function NavigationBarBottom({ children }: NavigationBarBottomProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useSyncHeight(
    containerRef,
    setNavigationBottomBarHeight,
    resetNavigationBottomBarHeight,
  );

  return (
    <NavigationBarContainerBottom ref={containerRef}>
      {children}
    </NavigationBarContainerBottom>
  );
}

interface NavigationBarBannerProps {
  children: ReactNode;
}

// The banner sits above the page content, so pages sized with
// `viewportHeight()` need its height to keep their bottom content visible.
function NavigationBarBanner({ children }: NavigationBarBannerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useSyncHeight(
    containerRef,
    setNavigationContentBannerHeight,
    resetNavigationContentBannerHeight,
  );

  return (
    <NavigationBarContainerBanner ref={containerRef}>
      {children}
    </NavigationBarContainerBanner>
  );
}

interface NavigationBarContainerProps {
  children?: ReactNode;
  banner?: ReactNode;
  navigationBar: ReactChild;
  mobileMenuBar: ReactChild;
}

export function NavigationBarContainer({
  children,
  banner,
  navigationBar,
  mobileMenuBar,
}: NavigationBarContainerProps) {
  const screen = useResponsiveValue('mobile', 'tablet', 'desktop');
  const isMobile = screen === 'mobile' || screen === 'tablet';

  return (
    <NavigationBarContainerRoot>
      {!isMobile && navigationBar}
      <NavigationBarContainerMain>
        <Paper>
          <NavigationBarContainerContent>
            {banner && <NavigationBarBanner>{banner}</NavigationBarBanner>}
            <Suspense fallback={<ContentProgressIndicator />}>
              {children}
            </Suspense>
          </NavigationBarContainerContent>
        </Paper>
        {isMobile && <NavigationBarBottom>{mobileMenuBar}</NavigationBarBottom>}
      </NavigationBarContainerMain>
    </NavigationBarContainerRoot>
  );
}

function useSyncHeight(
  ref: RefObject<HTMLElement>,
  setHeight: (heightPx: number) => void,
  resetHeight: () => void,
) {
  useLayoutEffect(() => {
    const element = ref.current!;

    function updateHeight() {
      if (!ref.current) {
        return;
      }

      setHeight(ref.current.offsetHeight);
    }

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        resetHeight();
      };
    }

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      resetHeight();
    };
  }, [ref, setHeight, resetHeight]);
}
